import { Box, Container, Divider, Grid, Link, Stack } from "@mui/material";
import React, { useState } from "react";
import SolarGoogleMap from "../SolarMap/SolarGoogleMap";
import Header from "../Header/Header";
import CustomerInput from "../CustomerInput/CustomerInput";
import GoalSelection from "../GoalSelection/GoalSelection";
import SolarModuleAmountChooser from "../SolarModuleAmountChooser/SolarModuleAmountChooser.tsx";
import AppointmentCall from "../AppointmentCall/AppointmentCall";
import ProductHeadline from "../ProductHeadline/ProductHeadline";
import ProductList from "../ProductList/ProductList";
import SystemOverview from "../SystemOverview/SystemOverview";
import CostsCalculation from "../CostsCalculation/CostsCalculation";
import { resetFunnelDataInLocalStorage } from "../../utils/LocalStorageManager.ts";
import ProductManager from "../../lib/Product/ProductManager.ts";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { AvailableGoal, ProductType } from "@shared/interfaces/product";
import type { Coordinates, PowerGoal } from "../../types/solar";
import { projectManager } from "../../lib/Solar/ProjectManager.ts";
import type { BuildingInsightsResponse } from "../SolarMap/SolarAPI/solar.ts";
import type { SelectedProducts } from "../../types/product";
import { getKwPerYearAndModuleFromBuildingInsights } from "../../utils/solarProduction";
import {
    useAddressData,
    useDetailsData,
    useFunnelStep,
    useSolarMapData,
} from "../../context/FunnelDataContext";
import { FUNNEL_STEP_IDS } from "../../constants/funnelSteps";
import { createLogger } from "../../lib/logger.ts";

const logger = createLogger("SolarConfiguratorStep");

const GOOGLE_API_KEY: string = import.meta.env.VITE_GOOGLE_API_KEY!;
const MINIMUM_MODULE_COUNT = 8;

export type SolarConfiguratorStepData = {
    costsPerKwh: number,
    powerConsumption: number,
    moduleCount: number,
    powerGoal: PowerGoal,
    selectedProducts: SelectedProducts,
    selectedAppointment?: string
}

const PowerGoals: PowerGoal[] = [
    {
        id: AvailableGoal.BASIC,
        label: 'Stromsparer',
        neededPowerConsumptionPercentage: 0.5,
        formattedLabel: 'Strom&shy;sparer'
    },
    {
        id: AvailableGoal.ADVANCED,
        label: 'Selbstversorger',
        neededPowerConsumptionPercentage: 1.1,
        formattedLabel: 'Selbst&shy;versorger'
    },
    {
        id: AvailableGoal.HIGHEND,
        label: 'E-Mobility',
        neededPowerConsumptionPercentage: 2.0,
        formattedLabel: 'E-Mobility'
    }
];
const calculateModuleCount = (consumption: number, powerGoal: PowerGoal, kwPerYearAndModule: number) => {
    const consumptionYearly = consumption * powerGoal.neededPowerConsumptionPercentage;

    return Math.max(Math.ceil(consumptionYearly / kwPerYearAndModule), MINIMUM_MODULE_COUNT);
};

const SolarConfiguratorStep: React.FC = () => {
    const theme = useTheme();
    const { data: configuratorData, save: saveConfiguratorData } =
        useFunnelStep<SolarConfiguratorStepData>(FUNNEL_STEP_IDS.SOLAR_CONFIGURATOR);
    const addressData = useAddressData()!;
    const detailsData = useDetailsData();
    const solarMapData = useSolarMapData();
    const [buildingInsights] = useState<BuildingInsightsResponse | undefined>(solarMapData?.buildingInsights);
    const [kwPerYearAndModule] = useState<number>(getKwPerYearAndModuleFromBuildingInsights(buildingInsights!, ProductManager.getProductsByGoal(PowerGoals[1].id).find((product) => product.type === ProductType.SOLAR_PANEL)?.powerClass || 450));
    const initialPowerConsumption = detailsData?.selectedPowerConsumptionEstimate.value as number;
    const [stepData] = useState<SolarConfiguratorStepData>(configuratorData || {
        costsPerKwh: 0.35,
        powerConsumption: initialPowerConsumption,
        moduleCount: calculateModuleCount(initialPowerConsumption, PowerGoals[1], kwPerYearAndModule),
        powerGoal: PowerGoals[1], // Default to 'advanced'
        selectedProducts: ProductManager.setSelectedProductsByGoal(PowerGoals[1].id),
    });
    const [selectedProducts, setSelectedProducts] = useState<SelectedProducts>(stepData.selectedProducts);
    ProductManager.setSelectedProducts(selectedProducts);

    if (Object.keys(stepData).length < Object.keys({
        moduleCount: 0,
        powerGoal: PowerGoals[0],
        selectedProducts: {},
        powerConsumption: 0,
        costsPerKwh: 0
    } as SolarConfiguratorStepData).length) {
        resetFunnelDataInLocalStorage();
        window.location.reload();
    }

    const [address] = useState<AddressWithPrediction>(addressData.currentAddress);
    const [location] = useState<Coordinates>(address.location!);
    const [powerConsumption, setPowerConsumption] = useState<number>(stepData.powerConsumption);
    const [costsPerKwh, setCostsPerKwh] = useState<number>(stepData.costsPerKwh);
    const [selectedAppointment, setSelectedAppointment] = useState<string | undefined>(stepData.selectedAppointment);
    logger.debug("KW per year and module", { kwPerYearAndModule });
    const [selectedGoal, setSelectedGoal] = useState<PowerGoal>(stepData.powerGoal);
    projectManager.activate(address);
    const project = projectManager.getProject();
    logger.debug('Project found', { project });
    const [moduleCount, setModuleCount] = useState<number>(stepData.moduleCount);
    const [maxModuleCount/*, setMaxModuleCount*/] = useState<number>(project.data?.optimizerResult['SYSTEM_OPTIMISATION_TYPE_PV_ONLY']?.pvPlant?.maxNumberOfPanelsOnRooftop || stepData.moduleCount * 2);

    // const monthlyBilling = useRef<number>(stepData.powerConsumption / 12 * stepData.costsPerKwh);

    const onProductsUpdate = (products: SelectedProducts) => {
        ProductManager.setSelectedProducts(products);
        stepData.selectedProducts = ProductManager.getSelectedProducts();

        saveConfiguratorData(stepData);

        setSelectedProducts(stepData.selectedProducts);
    };

    const onCustomerInputChange = (newPowerConsumption: number, newCostsPerKwh: number) => {
        stepData.powerConsumption = newPowerConsumption;
        stepData.costsPerKwh = newCostsPerKwh;
        stepData.moduleCount = calculateModuleCount(stepData.powerConsumption, stepData.powerGoal, kwPerYearAndModule);

        saveConfiguratorData(stepData);

        setPowerConsumption(stepData.powerConsumption);
        setCostsPerKwh(stepData.costsPerKwh);
        setModuleCount(stepData.moduleCount);
    };

    const onGoalChange = (newPowerGoal: PowerGoal) => {
        stepData.powerGoal = newPowerGoal;
        stepData.moduleCount = calculateModuleCount(stepData.powerConsumption, stepData.powerGoal, kwPerYearAndModule);
        stepData.selectedProducts = ProductManager.setSelectedProductsByGoal(stepData.powerGoal.id);

        saveConfiguratorData(stepData);

        setSelectedGoal(stepData.powerGoal);
        setModuleCount(stepData.moduleCount);
        setSelectedProducts(stepData.selectedProducts);
    };

    const onAppointmentSubmit = (selectedTimestamp: string) => {
        stepData.selectedAppointment = selectedTimestamp;

        saveConfiguratorData(stepData);

        setSelectedAppointment(selectedTimestamp);
    }

    const onModuleCountChange = (moduleCount: number) => {
        stepData.moduleCount = moduleCount;

        saveConfiguratorData(stepData);

        setModuleCount(moduleCount);
    };
    return (
        <Box
            sx={{
                backgroundColor: "#F5F5F5",
                minHeight: "100vh",
                display: "flex",
                justifyContent: "center",
                p: 0,
                m: 0
            }}
        >
            <Container maxWidth={false} sx={{ maxWidth: 1920 }} disableGutters>
                <Stack spacing={2}>
                    {/* Header */}
                    <Header/>

                    {/* Main Content */}
                    <Box className="main" sx={{ px: { xs: 2, md: 6, xl: 12 }, py: 2 }}>
                        <Grid container spacing={{ xs: 2, md: 3, lg: 4 }} columns={{ xs: 4, sm: 8, md: 12 }}>
                            {/* Left Column - House Image */}
                            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
                                <Stack spacing={2} sx={{ height: '100%' }}>
                                    <SolarGoogleMap
                                        location={location}
                                        apiKey={GOOGLE_API_KEY}
                                        markerPosition={{ coordinates: location, doesMarkerEqualsLocation: false }}
                                        currentModuleCount={moduleCount}
                                        projectId={project.projectId}
                                        aspectRatio={useMediaQuery(theme.breakpoints.down("md")) ? "4 / 3" : undefined}
                                        showBox={true}
                                        setModuleCount={setModuleCount}
                                    />
                                    <Stack
                                        direction="row"
                                        spacing={2}
                                        divider={<Divider orientation="vertical" flexItem/>}
                                    >
                                        <Link href={
                                            import.meta.env.VITE_SHOPIFY_STOREFRONT_DOMAIN +
                                            '/pages/widerruf-formular'
                                        } target={"_blank"}>Widerruf</Link>
                                        <Link href="https://adrex.de/datenschutz/" target={"_blank"}>Datenschutz</Link>
                                        <Link href="https://adrex.de/agb-online-shop/" target={"_blank"}>AGB</Link>
                                        <Link href="https://adrex.de/barrierefreiheit-online-shop/" target={"_blank"}>
                                            Barrierefreiheit
                                        </Link>
                                        <Link href="https://adrex.de/impressum-online-shop/" target={"_blank"}>
                                            Impressum
                                        </Link>
                                    </Stack>
                                </Stack>
                            </Grid>

                            {/* Center Column - Configuration */}
                            <Grid size={{ xs: 12, md: 6, lg: 8 }}>
                                <Grid container spacing={4}>
                                    {/* Center Column - Configuration */}
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Stack spacing={5} flexGrow={1} sx={{ height: '100%' }}>
                                            <CustomerInput
                                                address={address}
                                                currentPowerConsumption={powerConsumption}
                                                currentCostsPerKwh={costsPerKwh}
                                                onSubmit={onCustomerInputChange}
                                            />
                                            <GoalSelection
                                                currentGoal={selectedGoal}
                                                availableGoals={PowerGoals}
                                                onGoalChange={onGoalChange}
                                            />
                                            <SolarModuleAmountChooser
                                                currentModuleCount={moduleCount}
                                                onModuleCountChange={onModuleCountChange}
                                                maxModuleCount={maxModuleCount}
                                            />
                                            {/* <Box flexGrow={1} /> */}
                                            <AppointmentCall
                                                currentISODateString={selectedAppointment}
                                                onSubmit={onAppointmentSubmit}
                                            />
                                            {/* <BookmarkConfig /> */}
                                        </Stack>
                                    </Grid>

                                    {/* Right Column - Customization */}
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Stack spacing={1.5}>
                                            <SystemOverview
                                                moduleCount={moduleCount}
                                                selectedProducts={selectedProducts}
                                                kwPerYearAndModule={kwPerYearAndModule}
                                                powerConsumption={powerConsumption}
                                                costsPerKwh={costsPerKwh}
                                                address={address}
                                                pvSize={moduleCount * (selectedProducts[ProductType.SOLAR_PANEL]?.powerClass || 0) / 1000}
                                            />
                                            <ProductHeadline/>
                                            <ProductList
                                                selectedGoal={selectedGoal.id}
                                                selectedProducts={selectedProducts}
                                                onProductsUpdate={onProductsUpdate}
                                            />
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                    {/* Bottom Section - Cost Breakdown */}
                    <Box sx={{ position: "sticky", bottom: 0 }}>
                        <CostsCalculation
                            moduleCount={moduleCount}
                            selectedProducts={selectedProducts}
                            selectedAppointment={selectedAppointment}
                            kwPerYearAndModule={kwPerYearAndModule}
                            powerConsumption={powerConsumption}
                            costsPerKwh={costsPerKwh}
                        />
                    </Box>
                </Stack>
            </Container>
        </Box>
    );
};

export default SolarConfiguratorStep;
